/**
 * "A new version of Borough Heat is out — tap to reload" (docs/ONLINE-CLIENT.md "After a deploy").
 *
 * Shown when the server speaks a newer protocol than this page (it refused our hello with kick `protocol`, or
 * `/api/shards` says so at boot). The reload must really fetch the new build: index.html is served `no-cache`
 * (deploy/Caddyfile), the hashed assets are immutable, and before reloading we ask the browser to revalidate the
 * page itself (`cache: 'reload'`), so even a stale heuristic cache entry from before that header is replaced.
 */
import './feedback.css';

export function isServerNewer(serverProtocol: unknown, clientProtocol: number): boolean {
  return typeof serverProtocol === 'number' && Number.isInteger(serverProtocol) && serverProtocol > clientProtocol;
}

/**
 * The protocol a refused hello names: the server's `kick protocol` says "server speaks protocol N" for a version
 * mismatch, but the same kick reason also covers a malformed hello and a server-side error while joining
 * (server/net/gateway.ts). Only a named, newer protocol is a reason to reload; null otherwise.
 */
export function protocolFromKick(text: unknown): number | null {
  const m = /\bprotocol (\d{1,6})\b/.exec(typeof text === 'string' ? text : '');
  return m ? Number(m[1]) : null;
}

/** Revalidate the page (and its manifest) in the HTTP cache, then reload. */
export async function reloadToNewBuild(loc: Location = location, fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<void> {
  try {
    await Promise.race([
      Promise.all([fetchImpl(loc.pathname || './', { cache: 'reload', credentials: 'same-origin' }), fetchImpl('manifest.webmanifest', { cache: 'reload' }).catch(() => undefined)]),
      new Promise((r) => setTimeout(r, 2500)),
    ]);
  } catch {
    /* offline right now: the reload will tell */
  }
  loc.reload();
}

export class UpdateCard {
  readonly root: HTMLElement;

  constructor(parent: HTMLElement, reason: 'protocol' | 'build', onReload: () => void, onLater?: () => void) {
    this.root = document.createElement('div');
    this.root.className = 'bh-ui bh-update';
    this.root.setAttribute('role', 'alertdialog');
    this.root.setAttribute('aria-labelledby', 'bh-update-title');
    const card = document.createElement('div');
    card.className = 'card bh-glass blur';
    const h = document.createElement('h2');
    h.id = 'bh-update-title';
    h.textContent = 'A new version of Borough Heat is out';
    const p = document.createElement('p');
    p.textContent =
      reason === 'protocol'
        ? 'The city has been updated and this page is the old one. Tap to reload — it takes a few seconds, and your character, money and cars are safe on the server.'
        : 'The city has been updated. Reload when it suits you — your character, money and cars are safe on the server.';
    const actions = document.createElement('div');
    actions.className = 'actions';
    const go = document.createElement('button');
    go.type = 'button';
    go.className = 'go';
    go.textContent = 'Reload';
    go.addEventListener('click', () => {
      go.disabled = true;
      go.textContent = 'Reloading…';
      onReload();
    });
    actions.appendChild(go);
    if (onLater) {
      const later = document.createElement('button');
      later.type = 'button';
      later.className = 'back';
      later.textContent = 'Later';
      later.addEventListener('click', onLater);
      actions.appendChild(later);
    }
    card.append(h, p, actions);
    this.root.appendChild(card);
    // The whole card is the target on touch: "tap to reload".
    if (reason === 'protocol') card.addEventListener('click', (e) => e.target === card && go.click());
    this.root.addEventListener('keydown', (e) => e.stopPropagation());
    parent.appendChild(this.root);
    setTimeout(() => go.focus(), 0);
  }

  dispose(): void {
    this.root.remove();
  }
}
